import React, { useContext, useEffect } from "react";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import IOwTableCols from "../../../models/IOwTableCols";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import IDashboardItem from "../../../models/IDashboardItem";
import styled from "styled-components";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import IDashboardRefreshWidgetDataDTO from "../../../models/DTO/IDashboardRefreshWidgetDataDTO";
import DashboardWidgetTypeEnum from "../../../models/DashboardWidgetTypeEnum";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IDashboardOnRefreshWidgetDataDTO from "../../../models/DTO/IDashboardOnRefreshWidgetDataDTO";
import IDashboard from "../../../models/IDashboard";
import ISalesSummaryDataRow from "../../../models/ISalesSummaryDataRow";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { OwIconTooltip } from "../../shared/OwIconTooltip/OwIconTooltip";
import SupplierService from "../../../services/SupplierService";
import UserService from "../../../services/UserService";
import UserTypesEnum from "../../../models/UserTypesEnum";
import AccessTypesEnum from "../../../models/AccessTypesEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    inEditor: boolean;
    widget?: IDashboardItem;
    removeWidget?: () => void;
    updateWidgetFn?: (widgetData: any, widgetUuid: string) => void;
    dashboard?: IDashboard;
};

export const SalesSummaryWidget = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const intl = useIntl();

    const getDashboardDataForWidget = () => {
        let data = [];

        if (userModel.isSupplierUser) {
            if (props.widget && props.dashboard) {
                if (props.dashboard.data) {
                    if (props.dashboard.data[props.widget.uuid]) {
                        data = props.dashboard.data[props.widget.uuid].data;
                    }
                }
            }

            if (userModel.userType?.id === UserTypesEnum.SALES || userModel.supplierUserToken?.is_sales_rep) {
                let limitToUserId = true;

                if (userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.FULL) {
                    limitToUserId = false;
                }

                if (limitToUserId) {
                    data = data.filter((row: ISalesSummaryDataRow) => {
                        return row.userGuid === userModel.guid;
                    });
                }
            }
        }

        return data;
    };

    const getDashboardLastUpdatedAtForWidget = () => {
        if (props.widget && props.dashboard) {
            if (props.dashboard.data) {
                if (props.dashboard.data[props.widget.uuid]) {
                    return props.dashboard.data[props.widget.uuid].lastUpdatedAt;
                }
            }
        }

        return "";
    };

    const refreshData = () => {
        if (props.widget && props.dashboard) {
            const payload: IDashboardRefreshWidgetDataDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                widgetType: DashboardWidgetTypeEnum.SALES_SUMMARY,
                widgetUuid: props.widget?.uuid,
                dashboardId: props.dashboard.id,
            };

            props.socket.emit(EndpointsEnum.DashboardRefreshWidgetData, payload);
        }

        setIsRefreshingData(true);
    };

    const onRefreshData = (data: IDashboardOnRefreshWidgetDataDTO) => {
        const notification: INotification = {
            title: "sales_summary_queued_for_update",
            message: "please_allow_5_to_8_minutes_for_the_data_to_update",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        setIsRefreshingData(false);
    };

    const [isDataLoading, setIsDataLoading] = React.useState<boolean>(false);
    const [isInitialData, setIsInitialData] = React.useState<boolean>(false);
    const [isRefreshingData, setIsRefreshingData] = React.useState<boolean>(false);
    const [salesSummaryRows, setSalesSummaryRows] = React.useState<ISalesSummaryDataRow[]>(getDashboardDataForWidget());
    const [lastUpdatedAt, setLasUpdatedAt] = React.useState<string>(getDashboardLastUpdatedAtForWidget());

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.DashboardOnRefreshWidgetData, fn: onRefreshData }];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const renderNumberVal = (value: number) => {
        return <FormattedNumber value={value || 0} maximumFractionDigits={2} minimumFractionDigits={2} />;
    };

    const renderOrdersVal = (value: number) => {
        return <FormattedNumber value={value || 0} maximumFractionDigits={0} minimumFractionDigits={0} />;
    };

    const fiscalYearStartMonth = SupplierService.getFiscalYearStartMonth(supplierModel);
    const isFiscal = fiscalYearStartMonth !== 1;

    const renderFiscalTitle = (col: IOwTableCols) => {
        return (
            <span className={"text-nowrap"}>
                <OwLabel title={col.title} noBottomMargin={true} />
                <span className={"ms-1"}>
                    <OwIconTooltip
                        iconClassName={"fad fa-info-circle"}
                        tooltipContent={
                            <span>
                                <FormattedMessage id={"fiscal_year_starting"} defaultMessage={"Fiscal year starting"} />{" "}
                                {SupplierService.getFiscalYearStartLabel(fiscalYearStartMonth, intl.locale)}
                            </span>
                        }
                    />
                </span>
            </span>
        );
    };

    const salesSummaryColumns: IOwTableCols[] = [
        { title: "name", valueProp: "name" },
        { title: "wtd", valueProp: "wtd", renderFn: renderNumberVal, className: "text-end" },
        { title: "mtd", valueProp: "mtd", renderFn: renderNumberVal, className: "text-end" },
        {
            title: isFiscal ? "fqtd" : "qtd",
            valueProp: "qtd",
            renderFn: renderNumberVal,
            className: "text-end",
            titleRenderFn: isFiscal ? renderFiscalTitle : undefined,
        },
        {
            title: isFiscal ? "fytd" : "ytd",
            valueProp: "ytd",
            renderFn: renderNumberVal,
            className: "text-end",
            titleRenderFn: isFiscal ? renderFiscalTitle : undefined,
        },
        { title: "num_otd", valueProp: "numOrdersYtd", renderFn: renderOrdersVal, className: "text-end" },
    ];

    return (
        <WidgetContainer className={"SalesSummaryWidget widget h-100 d-flex flex-column justify-content-start"}>
            {props.inEditor ? (
                <div>
                    <div className={"w-100"}>
                        <div>
                            <OwIconButton
                                iconClass={"fas fa-trash-alt"}
                                iconColorClass={"text-danger"}
                                fn={() => {
                                    props.removeWidget!();
                                }}
                            />
                        </div>
                        <div>
                            <OwLabel title={"sales_summary"} />
                        </div>
                    </div>
                </div>
            ) : (
                <div className={"w-100"}>
                    <div className={"d-flex justify-content-between align-items-center"}>
                        <OwTitle title={"sales_summary"} tag={"h5"} />
                        <div>
                            <div className={"text-end"}>
                                <OwLabel
                                    title={"refresh"}
                                    className={"text-primary"}
                                    leftIconClass={"fad fa-refresh " + (isRefreshingData ? "fa-spin" : "")}
                                    fn={refreshData}
                                />
                            </div>
                            <div className={"text-small text-muted"}>
                                <OwLabel title={"last_updated"} />{" "}
                                {lastUpdatedAt.length ? UserService.parseLastUpdated(lastUpdatedAt, true, false, true) : "--"}
                            </div>
                        </div>
                    </div>
                    <OwFnTable
                        tableClassName={"w-100"}
                        columns={salesSummaryColumns}
                        dataSet={salesSummaryRows}
                        dataFetch={isDataLoading}
                        initialData={isInitialData}
                        wrappingClassName={"w-100"}
                    />
                </div>
            )}
        </WidgetContainer>
    );
};

const WidgetContainer = styled.div`
    padding: 0.5rem;
    justify-content: start !important;
    align-items: start !important;
    height: 100%;

    input {
        width: 100%;
    }

    .position-relative {
        width: 100%;
    }
`;
